import { useRef, useState, useEffect } from "react";
import { ImagePlus, Loader2, Trash2, Images } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { getFileUrl, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { PATIENTS } from "@/constants/testIds";
import { PHOTO_TYPE_LABELS } from "@/lib/patientConstants";

/** Galeria de fotos antes/despues del paciente, con upload directo al
 * Object Storage y borrado (admin/profesional). */
export default function PhotoGallery({ patientId, canManage }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [photoType, setPhotoType] = useState("before");
  const [caption, setCaption] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const inputRef = useRef(null);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/patients/${patientId}/photos`);
      setPhotos(data);
    } catch (err) {
      toast({ title: "Error al cargar fotos", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data: uploadData } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      await api.post(`/patients/${patientId}/photos`, { photo_url: uploadData.url, photo_type: photoType, caption: caption.trim() || null });
      toast({ title: "Foto agregada" });
      setCaption("");
      load();
    } catch (err) {
      toast({ title: "Error al subir la foto", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/patients/${patientId}/photos/${deleteTarget.id}`);
      toast({ title: "Foto eliminada" });
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-[0.1em] text-muted-foreground font-medium">Fotos antes / despues</h3>
      </div>

      {canManage && (
        <div className="flex items-center gap-2 flex-wrap bg-white border border-border rounded-xl p-3">
          <Select value={photoType} onValueChange={setPhotoType}>
            <SelectTrigger className="w-32 h-9" data-testid={PATIENTS.photoTypeSelect}><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(PHOTO_TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input
            className="flex-1 min-w-[140px] h-9"
            placeholder="Descripción (opcional)"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            data-testid={PATIENTS.photoCaptionInput}
          />
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" className="hidden" onChange={handleFile} data-testid={PATIENTS.photoUploadInput} />
          <Button type="button" size="sm" disabled={uploading} onClick={() => inputRef.current?.click()} data-testid={PATIENTS.photoSaveButton}>
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
            {uploading ? "Subiendo..." : "Subir foto"}
          </Button>
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground text-sm">Cargando...</p>
      ) : photos.length === 0 ? (
        <div className="bg-white border border-border rounded-xl p-8 text-center" data-testid={PATIENTS.photoEmptyState}>
          <Images className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Todavía no hay fotos cargadas.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid={PATIENTS.photoGrid}>
          {photos.map((p) => (
            <div key={p.id} className="relative group rounded-lg overflow-hidden border border-border bg-white" data-testid={`${PATIENTS.photoItemPrefix}${p.id}`}>
              <img src={getFileUrl(p.photo_url)} alt={p.caption || p.photo_type} className="w-full h-32 object-cover" />
              <span className="absolute top-1.5 left-1.5 text-[10px] uppercase tracking-wide bg-black/60 text-white px-1.5 py-0.5 rounded">
                {PHOTO_TYPE_LABELS[p.photo_type]}
              </span>
              {canManage && (
                <button
                  type="button"
                  onClick={() => setDeleteTarget(p)}
                  className="absolute top-1.5 right-1.5 bg-black/60 hover:bg-destructive text-white rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`${PATIENTS.photoDeleteButtonPrefix}${p.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
              {p.caption && <p className="text-[11px] text-muted-foreground p-1.5 truncate">{p.caption}</p>}
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar esta foto?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
